import React, { useEffect } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';

import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { LoginCallback, Security } from '@okta/okta-react';

import '@getethos/ethos-design-system-v2';
import { Loading } from '../common/components/Loading';
import { Resource } from '../common/constants/resource';
import { FeatureFlagsWrapper } from '../common/featureFlagsWrapper';
import { AuthWrapper } from '../features/login/AuthWrapper';
import Routing from './AppRoutes';
import style from './styles.module.scss';
import { useClientConfig } from './useClientConfig';

import '../datadogRum';

export const SecureRoutes = ({ clientConfig }: { clientConfig: any }) => {
  const oktaAuth = new OktaAuth(clientConfig.securityProps);

  const navigate = useNavigate();
  const restoreOriginalUri = (_oktaAuth: any, originalUri: string) => {
    navigate(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <AuthWrapper>
        <Routes>
          <Route
            path="implicit/callback"
            element={<LoginCallback loadingElement={<Loading fullscreen />} />}
          />
          <Route path="/*" element={<Routing />} />
        </Routes>
      </AuthWrapper>
    </Security>
  );
};

export const App = () => {
  useEffect(() => {
    document.title = Resource.APPLICATION_TITLE;
  });

  const clientConfig = useClientConfig();

  if (clientConfig.error) {
    const err: unknown = clientConfig.error;
    const errMsg =
      err instanceof Error
        ? err.message
        : typeof err === 'string'
          ? err
          : err &&
              typeof err === 'object' &&
              'message' in err &&
              typeof (err as { message?: unknown }).message === 'string'
            ? (err as { message: string }).message
            : null;
    return (
      <>
        <p>Unexpected error occurred. Please contact your administrator.</p>
        {process.env.NODE_ENV === 'development' && (
          <p style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
            {errMsg ? `Details: ${errMsg}. ` : ''}
            Check the browser console and ensure the backend is running at the
            configured API URL.
          </p>
        )}
      </>
    );
  }

  if (!clientConfig.isReady) {
    return <Loading fullscreen={true} />;
  }

  return (
    <div className={style.Container}>
      <FeatureFlagsWrapper flags={clientConfig.flags}>
        <SecureRoutes clientConfig={clientConfig} />
      </FeatureFlagsWrapper>
    </div>
  );
};
